import { flatten, groupBy, sortBy, values } from 'lodash-es'
import { useState } from 'react'
import { useParams } from 'react-router'
import { useLocalStorage } from 'usehooks-ts'

import { FvLinkButton, Icon } from '@fv/client-components'
import type { FullShipment, Quote } from '@fv/client-types'
import type { Currency } from '@fv/models'
import { quoteUtils } from '@fv/models/core'

import { DividedContent } from '@/components/DividedContent'
import InfoBox from '@/components/InfoBox'
import { SelectField } from '@/components/inputs/SelectField'
import YesNoToggle from '@/components/inputs/YesNoToggle'
import { useExchangeRatesForLoad } from '@/hooks/useExchangeRate'
import { routes } from '@/routes'
import { isBookedAndDispatched } from '@/utils/shipmentFuncs'

import { HasPermission } from '../auth'
import { HideForVendor } from '../auth/HasRole'
import { useRole } from '../auth/hooks'
import { SpotQuotePanel } from '../spot-quote/SpotQuotePanel'
import { CurrencySelector } from './CurrencySelector'
import { AddRateButton } from './ManualRatePanel'
import { PrintRatesButton } from './PrintRatesButton'
import { quoteFuncs } from './quoteFuncs'
import { RateAlerts } from './RateAlerts'
import { PendingRate, RateGroup, RateRow } from './RateGroup'
import {
  getCarrierIdentifier,
  type RateListingData,
  rateSortTypes,
  type SortType,
  useRateListing,
} from './useRatesListing'
import { VendorRatesFooter } from './VendorRatesFooter'

type Props = {
  load: FullShipment
  rates?: Quote[]
}

type GroupByType = 'carrier' | 'broker'

export const RatesListing = ({ load, rates }: Props) => {
  const [expanded, setExpanded] = useState(false)
  const [saveRateId, setSaveRateId] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [displayCurrency, setDisplayCurrency] = useState<Currency | null>(null)
  const [sortType, setSortBy] = useState<SortType>('cheapest')
  const [groupBy, setGroupBy] = useLocalStorage<GroupByType>(
    'ltl-rates-groupby',
    'carrier',
  )
  const exchangeRates = useExchangeRatesForLoad(load, !!displayCurrency)
  const { mode } = useParams<{ mode?: string }>()
  const role = useRole()
  const isReadOnly = mode === 'view'
  const availableRates =
    isBookedAndDispatched(load) && !isReadOnly
      ? rates?.filter(r => r.carrierId === load.selectedQuote?.carrierId)
      : rates
  const hasNonUsdRates = !!availableRates?.some(
    r => r.currency && r.currency !== 'usd',
  )
  const rateListingData = useRateListing(load, availableRates, sortType)
  const { activeRateGroups, erroredRates, pendingApiRates } = useLTLRateGroups(
    rateListingData,
    groupBy,
  )

  const hasAnyDirectRates =
    activeRateGroups.length || erroredRates.length || pendingApiRates.length
  const getRateGroupKey = (group: Quote[]) => {
    const keyBase = `${getCarrierIdentifier(group[0], groupBy)}_${group[0].method}`

    if (group[0].method === 'manual') {
      // we don't group manual quotes
      return `${keyBase}_${group[0]._id}`
    }
    return keyBase
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      {!isReadOnly &&
        (load.workflow === 'ltl' || load.workflow === 'parcel') && (
          <RateAlerts load={load} rates={rates} />
        )}

      <div className="ltl-rates-ctn relative min-h-0 w-full flex-1 overflow-auto bg-fv-gray-50 px-6 py-8 b1200:pb-20 [scrollbar-gutter:stable]">
        <div className="mb-6 flex gap-x-2">
          {!isReadOnly && (
            <>
              <SelectField
                name="sortBy"
                className="w-auto"
                value={sortType}
                onChange={e => setSortBy(e.target.value as SortType)}
                options={rateSortTypes.map(s => ({
                  text: `Sort by ${s.type}`,
                  value: s.type,
                }))}
              />

              {load.workflow === 'ltl' && (
                <SelectField
                  className="w-auto"
                  name="groupBy"
                  value={groupBy}
                  onChange={e => setGroupBy(e.target.value as GroupByType)}
                  options={[
                    { text: 'Group by carrier', value: 'carrier' },
                    { text: 'Group by broker', value: 'broker' },
                  ]}
                />
              )}
              <CurrencySelector
                value={displayCurrency}
                onChange={setDisplayCurrency}
                locations={load.locations}
                className="w-auto"
              />
            </>
          )}
          <DividedContent className="ml-1">
            {isReadOnly && (
              <FvLinkButton
                to={routes.details(load.loadId)}
                icon="arrow-left"
                className="whitespace-nowrap px-3"
              >
                Back to details
              </FvLinkButton>
            )}
            <div className="flex items-center">
              <label htmlFor="expanded">
                <span className="b1450:hidden">Expand all rate details</span>
                <span className="hidden b1450:inline">Expand</span>
              </label>

              <YesNoToggle
                onOff
                checked={expanded}
                className="ml-2"
                name="expanded"
                onChange={e => setExpanded(e.target.checked)}
              />
            </div>
            <HasPermission name="view-rates">
              <PrintRatesButton
                buttonTextClass="b1200:hidden"
                loadId={load.loadId}
                className="whitespace-nowrap px-3"
              >
                Print rates
              </PrintRatesButton>
            </HasPermission>
            <HideForVendor>
              {load.status === 'pending' &&
                load.workflow === 'ltl' &&
                !isReadOnly && (
                  <AddRateButton icon="plus" load={load} rateType="manual-rate">
                    <span className="b1200:hidden">Add rate</span>
                  </AddRateButton>
                )}
            </HideForVendor>
          </DividedContent>
        </div>

        {(load.workflow === 'ltl' || load.workflow === 'truckload') &&
          activeRateGroups.map(group => (
            <RateGroup
              expanded={expanded}
              key={getRateGroupKey(group)}
              load={load}
              locations={load.locations}
              rates={group}
              saveRateId={saveRateId}
              setSaveRateId={setSaveRateId}
              isReadOnly={isReadOnly}
              pickupDate={load.pickupDate}
              displayCurrency={displayCurrency}
              exchangeRates={exchangeRates}
              showCurrency={hasNonUsdRates}
            />
          ))}
        {load.workflow === 'parcel' &&
          rateListingData.activeApiRates.map(r => (
            <div className="mb-3" key={r._id}>
              <RateRow
                load={load}
                isReadOnly={isReadOnly}
                saveRateId={saveRateId}
                setSaveRateId={setSaveRateId}
                locations={load.locations}
                expanded={expanded}
                highlight={r.status === 'awarded'}
                rate={r}
                pickupDate={load.pickupDate}
                displayCurrency={displayCurrency}
                exchangeRates={exchangeRates}
                showCurrency={hasNonUsdRates}
              />
            </div>
          ))}

        {pendingApiRates.map(r => (
          <PendingRate
            key={`pending-${getCarrierIdentifier(r, groupBy)}`}
            carrierName={r.providerName}
          />
        ))}

        {!activeRateGroups.length && !pendingApiRates.length && (
          <InfoBox>
            No rates available for this shipment.
            <HideForVendor>
              {' '}
              You can either{' '}
              <AddRateButton
                className="underline decoration-fv-blue underline-offset-4 hover:decoration-inherit"
                load={load}
                rateType="manual-rate"
              >
                add a manual rate
              </AddRateButton>{' '}
              or request spot quotes.
            </HideForVendor>
          </InfoBox>
        )}
        <HideForVendor>
          {!!erroredRates.length && load.status === 'pending' && (
            <DividedContent className="b1200:mt-8 mt-6">
              <div>{erroredRates.length} carriers did not return rates</div>
              <FvLinkButton
                className="pl-4"
                icon="arrows-from-line"
                onClick={() => setShowErrors(p => !p)}
              >
                Show carriers
              </FvLinkButton>
            </DividedContent>
          )}
        </HideForVendor>

        {showErrors && (
          <div className="mb-3">
            {flatten(erroredRates).map(r => {
              return (
                <div className="mt-4 flex flex-nowrap" key={r._id}>
                  <InfoBox className="mb-0 w-full" custom>
                    <div className="bg-fv-beer-xlight inline-block border-fv-beer-medium border p-1">
                      {r.providerName}
                    </div>
                    <div className="text-xs mt-1 ml-[.35rem]">
                      Your direct rate
                    </div>

                    <div className="flex border-t border-dotted border-fv-gray mt-2 pt-3">
                      <Icon
                        className="text-fv-orange"
                        icon="exclamation-triangle"
                        transform="down-2"
                      />
                      <p className="mb-0 leading-6">{r.errors?.[0]?.message}</p>
                    </div>
                  </InfoBox>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {!isReadOnly && load.workflow !== 'parcel' && role !== 'vendor' && (
        <SpotQuotePanel
          defaultOpen={!hasAnyDirectRates}
          label="Get more rates using spot quotes"
          loads={load ? [load] : []}
        />
      )}

      <VendorRatesFooter />
    </div>
  )
}

function useLTLRateGroups(
  { manualRates, activeApiRates, pendingRates, erroredRates }: RateListingData,
  groupByType: GroupByType,
): {
  activeRateGroups: Quote[][]
  erroredRates: Quote[][]
  pendingApiRates: Quote[]
} {
  // Add API rates under manual rates
  const ratesByCarrier = activeApiRates.reduce((groups, rate) => {
    const carrierIdentifier = getCarrierIdentifier(rate, groupByType)
    const rateGroup = groups.find(
      g => getCarrierIdentifier(g[0], groupByType) === carrierIdentifier,
    )

    if (!rateGroup) {
      const carrierRates = activeApiRates.filter(
        r => getCarrierIdentifier(r, groupByType) === carrierIdentifier,
      )

      groups.push(carrierRates)
    }

    return groups
  }, [] as Quote[][])

  const activeRateGroups: Quote[][] = [
    ...manualRates.map(r => [r]),
    ...ratesByCarrier,
  ]

  // Group errored by carriers that don't already have successful rates
  const errorsWithoutSuccesses = erroredRates.filter(
    e =>
      !activeRateGroups.some(group => group[0].providerName === e.providerName),
  )
  const errorsByCarrier = groupBy(errorsWithoutSuccesses, q => q.providerName)

  return {
    activeRateGroups,
    erroredRates: sortBy(values(errorsByCarrier), rates =>
      quoteFuncs.carrierSort(rates[0]),
    ).map(q => (quoteUtils.isBrokerQuote(q[0]) ? [q[0]] : q)),
    pendingApiRates: pendingRates,
  }
}
